import { useCallback, useEffect, useRef, useState } from 'react'
import { BREAKPOINT, ThemeContextValue, uiPrimitives, useThemedStyles } from '../theme'
import { navigateHash } from '../navigate'
import { ApiError, CurrentUser, refreshSessionUser } from './api'
import { PLAN_FEATURES, PLUS_FEATURE_NAMES, PlanFeatureRow, PlusFeature, isPlusUser } from './plan'
import {
	DEFAULT_MONTHS,
	FALLBACK_PRICES,
	PayOrder,
	PayPrice,
	createOrder,
	fetchOrder,
	fetchPayOptions,
	fmtExpiry,
	isWechatBrowser,
} from './pay'

/**
 * 升级 Plus 会员的弹窗。
 *
 * 四件事：说清**为什么弹**（哪个入口被挡住了）、摆出**两档差异**、
 * 摆出**三档价格**、完成**扫码付款**。版式沿用 LegalDocModal / NoticeModal
 * 那套（backdrop / paper2 / 衬线字体），深浅主题自动跟随。
 *
 * 对比表的数据在 `saas/plan.ts`，价格与支付接口在 `saas/pay.ts`，这里只管画。
 *
 * ## 支付这一段为什么长这样
 *
 * 微信支付走的是 Native（扫码）：后端下单拿 `code_url`，这里画成二维码，
 * 用户用微信扫。**没有跳转、没有回调页面** —— 付完这个弹窗自己会发现
 * （每 2 秒问一次订单状态），然后变成"已开通"。
 *
 * 用户切走再回来也没关系：订单在库里是 pending，微信那边付了之后回调会核销，
 * 下次打开弹窗（或刷新页面）看到的就是 Plus。
 *
 * ⚠️ 手机上二维码扫不了自己那块屏 —— 微信内置浏览器里会多一句"长按保存 →
 *    扫一扫 → 相册"的提示。这是 Native 支付的固有短板，要彻底解决得申请
 *    服务号走 JSAPI，那是另一件事（见 backend/manmantan/settings.py 的说明）。
 */
const stylesFactory = (t: ThemeContextValue) => ({
	...uiPrimitives(t),

	backdrop: {
		position: 'fixed' as const,
		// 不用 `inset:0` 简写：UC/微信内核（Chromium < 87）不认
		top: 0,
		right: 0,
		bottom: 0,
		left: 0,
		background: 'rgba(0,0,0,.42)',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		zIndex: 2000,
		padding: 16,
	},
	box: {
		background: t.palette.paper2,
		border: `1px solid ${t.palette.line}`,
		borderRadius: t.radius.lg,
		boxShadow: t.shadows.lg,
		display: 'flex',
		flexDirection: 'column' as const,
		width: 560,
		maxWidth: '100%',
		maxHeight: '88vh',
		color: t.palette.ink,
	},
	head: {
		padding: '20px 22px 14px',
		borderBottom: `1px solid ${t.palette.line}`,
		flex: 'none',
	},
	title: {
		fontFamily: t.fonts.serif,
		fontSize: 19,
		fontWeight: 700,
		margin: '0 0 6px',
	},
	sub: {
		fontFamily: t.fonts.serif,
		fontSize: 13,
		color: t.palette.ink2,
		lineHeight: 1.6,
	},
	/** 被挡住的那个入口名，单独加粗，让人一眼知道"我点的是这个" */
	feature: {
		fontWeight: 700,
		color: t.palette.cinnabar,
	},
	/** 已经是 Plus 时，标题下面那行"有效期至 …" */
	plusState: {
		marginTop: 6,
		fontFamily: t.fonts.serif,
		fontSize: 12,
		color: t.palette.ink3,
	},
	body: {
		padding: '16px 22px 8px',
		overflowY: 'auto' as const,
	},
	/**
	 * 表格改成「一行一个 block」而不是整张 grid。
	 *
	 * 原因：对比表里有些功能要带细分项（倍速、织体、对错统计…），那些文案
	 * 在 84/96px 的窄列里会折成三四行。用 block 包住「主行 + 细分行」，
	 * 细分行就能整行铺开（见 detail），主行仍保持三列对齐。
	 */
	table: {
		borderTop: `1px solid ${t.palette.line}`,
	},
	row: {
		borderBottom: `1px solid ${t.palette.line}`,
	},
	line: {
		display: 'grid' as const,
		gridTemplateColumns: '1fr 84px 96px',
		[`@media (max-width: ${BREAKPOINT}px)`]: {
			gridTemplateColumns: '1fr 72px 82px',
		},
	},
	headRow: {
		borderBottom: `1px solid ${t.palette.line}`,
	},
	th: {
		padding: '8px 6px',
		fontFamily: t.fonts.serif,
		fontSize: 12,
		fontWeight: 600,
		color: t.palette.ink3,
		textAlign: 'center' as const,
		whiteSpace: 'nowrap' as const,
	},
	thName: {
		textAlign: 'left' as const,
		paddingLeft: 4,
	},
	/** Plus 列整列染色：不用读两列标题也能分清哪边是「升级后」 */
	thPlus: {
		color: t.palette.cinnabar,
	},
	cell: {
		padding: '9px 6px',
		fontFamily: t.fonts.serif,
		fontSize: 12.5,
		lineHeight: 1.45,
		textAlign: 'center' as const,
		color: t.palette.ink2,
		alignSelf: 'stretch',
	},
	cellName: {
		textAlign: 'left' as const,
		paddingLeft: 4,
		color: t.palette.ink,
	},
	cellPlus: {
		background: 'rgba(168,68,58,.055)',
		color: t.palette.ink,
	},
	/** 细分行：整宽小字，左边一个归属标签，缩进一点表示从属上一行 */
	detail: {
		display: 'flex' as const,
		alignItems: 'flex-start' as const,
		gap: 6,
		padding: '0 6px 7px 14px',
		fontFamily: t.fonts.serif,
		fontSize: 11.5,
		lineHeight: 1.5,
		color: t.palette.ink2,
	},
	detailText: {
		flex: 1,
		minWidth: 0,
	},
	detailLabel: {
		color: t.palette.ink,
		fontWeight: 600,
		marginRight: 5,
	},
	/** 「Plus」「两档」小签。描边不用实底：一列实底小签会把版面压得很吵 */
	tag: {
		flexShrink: 0,
		marginTop: 1,
		padding: '0 3px',
		fontSize: 9.5,
		lineHeight: '14px',
		borderRadius: 2,
		border: '1px solid currentColor',
		fontWeight: 700,
		letterSpacing: '.02em',
	},
	tagPlus: {
		color: t.palette.cinnabar,
	},
	tagBoth: {
		color: t.palette.ink3,
	},
	yes: {
		color: t.palette.celadon,
		fontWeight: 700,
	},
	no: {
		color: t.palette.ink3,
	},
	hint: {
		margin: '12px 4px 4px',
		fontFamily: t.fonts.serif,
		fontSize: 12,
		color: t.palette.ink3,
		lineHeight: 1.6,
	},
	/** 对比表折叠开关：整宽、无底色，像一条分隔线，不像按钮 */
	tableToggle: {
		display: 'flex' as const,
		alignItems: 'center' as const,
		justifyContent: 'space-between' as const,
		width: '100%',
		margin: '14px 0 0',
		padding: '9px 2px 8px',
		borderTop: `1px solid ${t.palette.line}`,
		background: 'none',
		border: 'none',
		outline: 'none',
		color: t.palette.ink2,
		fontFamily: t.fonts.serif,
		fontSize: 12.5,
		textAlign: 'left' as const,
		cursor: 'pointer',
		'&:hover': {
			color: t.palette.ink,
		},
	},
	tableCaret: {
		flexShrink: 0,
		marginLeft: 8,
		fontSize: 9.5,
		color: t.palette.ink3,
	},

	// ── 购买区 ────────────────────────────────────────────────
	/**
	 * 购买区放在**弹窗顶部**（标题下、对比表之前）。
	 *
	 * 最初把它放在对比表下面，结果弹窗一打开只看见 10 行功能表，价格与
	 * 「立即支付」都在折线以下 —— 用户得先滚动才知道多少钱。而这一屏
	 * 唯一要用户做的事就是"选个档位、付钱"，价格不该藏在后面。
	 * 对比表因此改成默认折叠（见 tableToggle）。
	 */
	buyBlock: {
		paddingBottom: 4,
	},
	buyHd: {
		display: 'flex' as const,
		alignItems: 'baseline' as const,
		gap: 8,
		marginBottom: 9,
	},
	buyTitle: {
		fontFamily: t.fonts.serif,
		fontSize: 13.5,
		fontWeight: 700,
		color: t.palette.ink,
	},
	buySub: {
		fontFamily: t.fonts.serif,
		fontSize: 11.5,
		color: t.palette.ink3,
	},
	plans: {
		display: 'grid' as const,
		gridTemplateColumns: 'repeat(3, 1fr)',
		gap: 8,
		[`@media (max-width: ${BREAKPOINT}px)`]: {
			gap: 6,
		},
	},
	/**
	 * 档位卡。选中态只换**边框颜色 + 底色**，不挪位置、不改内边距 ——
	 * 加粗边框会让卡片抖一下，三张并排时特别明显。
	 */
	planCard: {
		position: 'relative' as const,
		display: 'flex' as const,
		flexDirection: 'column' as const,
		alignItems: 'center' as const,
		gap: 2,
		padding: '10px 6px 9px',
		border: `1px solid ${t.palette.line}`,
		borderRadius: t.radius.sm,
		background: t.palette.paper2,
		cursor: 'pointer',
		transition: 'border-color .12s, background .12s',
		'&:hover': {
			borderColor: t.palette.celadon,
		},
	},
	planCardOn: {
		borderColor: t.palette.cinnabar,
		// 与对比表 Plus 列同一层淡染，视觉上是一件事
		background: 'rgba(168,68,58,.055)',
	},
	planName: {
		fontFamily: t.fonts.serif,
		fontSize: 12.5,
		color: t.palette.ink2,
	},
	planPrice: {
		fontFamily: t.fonts.serif,
		fontSize: 20,
		fontWeight: 700,
		color: t.palette.ink,
		lineHeight: 1.2,
	},
	planUnit: {
		fontSize: 12,
		fontWeight: 400,
		marginRight: 1,
	},
	/**
	 * 档位卡上的小字。窄屏时这一列只有 100px，中文按字断行会把
	 * 「等于送一 / 个月」拆出孤字，所以按逗号切成两段渲染：
	 * 后一段 nowrap，让换行只发生在「，」后面（见 noteTail）。
	 */
	planNote: {
		fontFamily: t.fonts.serif,
		fontSize: 10.5,
		color: t.palette.ink3,
		textAlign: 'center' as const,
		lineHeight: 1.4,
		[`@media (max-width: ${BREAKPOINT}px)`]: {
			fontSize: 10,
			lineHeight: 1.35,
		},
	},
	noteTail: {
		whiteSpace: 'nowrap' as const,
	},
	/** 「常见」角标：贴右上角，不占布局空间 */
	planBadge: {
		position: 'absolute' as const,
		top: -7,
		right: -1,
		padding: '0 4px',
		fontSize: 9.5,
		lineHeight: '14px',
		borderRadius: 2,
		background: t.palette.cinnabar,
		color: '#fff',
		fontFamily: t.fonts.serif,
	},

	// ── 扫码 / 成功面板 ───────────────────────────────────────
	payPanel: {
		display: 'flex' as const,
		flexDirection: 'column' as const,
		alignItems: 'center' as const,
		gap: 10,
		padding: '18px 8px 6px',
	},
	payAmount: {
		fontFamily: t.fonts.serif,
		fontSize: 26,
		fontWeight: 700,
		color: t.palette.cinnabar,
		lineHeight: 1.1,
	},
	payAmountUnit: {
		fontSize: 15,
		fontWeight: 400,
		marginRight: 1,
	},
	payDesc: {
		fontFamily: t.fonts.serif,
		fontSize: 12.5,
		color: t.palette.ink2,
		textAlign: 'center' as const,
		lineHeight: 1.6,
	},
	/**
	 * 二维码容器固定 196×196：SVG 是矢量的，缩放到多大都不会糊，
	 * 但固定尺寸能让"等待支付"和"已支付"两个状态切换时不跳版。
	 */
	qrBox: {
		width: 196,
		height: 196,
		padding: 8,
		background: '#fff',
		border: `1px solid ${t.palette.line}`,
		borderRadius: t.radius.sm,
		display: 'flex' as const,
		alignItems: 'center' as const,
		justifyContent: 'center' as const,
	},
	qrImg: {
		display: 'block',
		width: '100%',
		height: '100%',
	},
	/** 二维码画不出来时的兜底：给原文，可复制 */
	qrFallback: {
		fontFamily: t.fonts.mono,
		fontSize: 10,
		color: t.palette.ink2,
		wordBreak: 'break-all' as const,
		textAlign: 'center' as const,
		lineHeight: 1.5,
	},
	payTips: {
		fontFamily: t.fonts.serif,
		fontSize: 11.5,
		color: t.palette.ink3,
		textAlign: 'center' as const,
		lineHeight: 1.6,
	},
	payOkMark: {
		width: 60,
		height: 60,
		borderRadius: '50%',
		border: `1.5px solid ${t.palette.celadon}`,
		color: t.palette.celadonInk,
		display: 'flex' as const,
		alignItems: 'center' as const,
		justifyContent: 'center' as const,
		fontSize: 26,
		marginTop: 6,
	},
	payErr: {
		margin: '4px 2px 0',
		fontFamily: t.fonts.serif,
		fontSize: 12,
		color: t.palette.cinnabar,
		lineHeight: 1.6,
	},
	acts: {
		padding: '14px 22px 18px',
		borderTop: `1px solid ${t.palette.line}`,
		display: 'flex',
		justifyContent: 'flex-end' as const,
		alignItems: 'center' as const,
		gap: 10,
		flex: 'none',
	},
	waiting: {
		marginRight: 'auto' as const,
		fontFamily: t.fonts.serif,
		fontSize: 12,
		color: t.palette.ink3,
	},
})

/**
 * 一个单元格的内容：布尔值画符号，字符串原样。
 *
 * 做成纯函数而不是组件 —— 每个单元格都调 `useThemedStyles` 会各自建一张
 * 样式表（23 行 × 3 列），白白多出几十份 <style>。样式类由调用方注入。
 */
function cellContent(value: string | boolean, classes: Record<string, string>) {
	if (value === true) return <span className={classes.yes}>✓</span>
	if (value === false) return <span className={classes.no}>—</span>
	return <>{value}</>
}

/** 弹窗处于哪一步 */
type Stage = 'idle' | 'creating' | 'waiting' | 'paid'

/**
 * 把「省 9 元，等于送一个月」切成 ["省 9 元，", "等于送一个月"]。
 *
 * 只为了排版：窄屏一列 100px，浏览器按字断行会切出「等于送一 / 个月」
 * 这种孤字。后段套 nowrap 后，换行只能落在逗号处，读起来完整。
 */
function splitNote(note: string): string[] {
	const i = note.indexOf('，')
	if (i < 0) return [note]
	return [note.slice(0, i + 1), note.slice(i + 1)]
}

/** 轮询间隔与上限：2 秒一次、最多 5 分钟（微信 Native 码 2 小时失效，够用） */
const POLL_MS = 2000
const POLL_MAX = 150

export function UpgradeDialog(props: {
	open: boolean
	/** 哪个入口触发的。只影响标题文案，不影响对比表 */
	feature?: PlusFeature
	onClose: () => void
	/** 当前登录者。用来判断"已开通"与"有效期至"，不传也能用（始终显示免费口径） */
	user?: CurrentUser | null
}) {
	const classes = useThemedStyles(stylesFactory)
	const {open, onClose, user} = props

	const [stage, setStage] = useState<Stage>('idle')
	const [months, setMonths] = useState(DEFAULT_MONTHS)
	// 先用兜底价渲染：等接口回来再换。否则弹窗第一帧价格是空的，
	// 按钮会显示成「立即支付」没有金额，看着像加载失败。
	const [prices, setPrices] = useState<PayPrice[]>(FALLBACK_PRICES)
	const [payEnabled, setPayEnabled] = useState(true)
	const [order, setOrder] = useState<PayOrder | null>(null)
	const [expires, setExpires] = useState<string | null>(null)
	const [error, setError] = useState('')
	/** 对比表默认收起：一打开就能看见价格与「立即支付」，想细看再展开 */
	const [showTable, setShowTable] = useState(false)
	/** 下单返回 401：给一句"先登录"，而不是把"请先登录"当成支付失败 */
	const [needLogin, setNeedLogin] = useState(false)

	const pollRef = useRef<number | null>(null)
	const pollCount = useRef(0)

	const stopPoll = useCallback(() => {
		if (pollRef.current !== null) {
			window.clearInterval(pollRef.current)
			pollRef.current = null
		}
	}, [])

	// 每次打开都复位：上一次的二维码、错误、成功状态都不该留到下一次
	useEffect(() => {
		if (!open) return
		setStage('idle')
		setOrder(null)
		setError('')
		setNeedLogin(false)
		setExpires(null)
		setShowTable(false)
		stopPoll()
		let alive = true
		void fetchPayOptions().then(r => {
			if (!alive) return
			setPrices(r.prices)
			setPayEnabled(r.enabled)
		})
		return () => { alive = false }
	}, [open, stopPoll])

	// 关闭时停掉轮询（弹窗关着还在每 2 秒打一次接口，纯属浪费）
	useEffect(() => {
		if (open) return
		stopPoll()
	}, [open, stopPoll])

	// Esc 关闭。document 监听：弹窗打开时焦点还留在触发它的菜单项上
	useEffect(() => {
		if (!open) return
		const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
		document.addEventListener('keydown', onKey)
		return () => document.removeEventListener('keydown', onKey)
	}, [open, onClose])

	// 卸载时兜底清掉定时器
	useEffect(() => stopPoll, [stopPoll])

	const startPay = async () => {
		setError('')
		setNeedLogin(false)
		setStage('creating')
		try {
			const created = await createOrder(months)
			setOrder(created)
			setStage('waiting')
		} catch (e) {
			const err = e as ApiError
			if (err?.isNotLoggedIn) {
				setNeedLogin(true)
			} else if (err?.code === 'pay_not_configured') {
				setPayEnabled(false)
			} else {
				setError(err?.message || '下单失败，请稍后重试')
			}
			setStage('idle')
		}
	}

	// 等待支付：每 2 秒问一次订单状态。付成功就切到"已开通"并刷新登录态
	// （顶栏的「免费会员」、库页的额度胶囊都吃这个刷新）。
	useEffect(() => {
		if (stage !== 'waiting' || !order) return
		pollCount.current = 0
		stopPoll()
		pollRef.current = window.setInterval(async () => {
			pollCount.current += 1
			if (pollCount.current > POLL_MAX) {
				stopPoll()
				setError('二维码已过期。重新点一次「立即支付」会生成新的')
				setStage('idle')
				return
			}
			try {
				const r = await fetchOrder(order.id)
				if (r.order.status === 'paid') {
					stopPoll()
					setExpires(r.plan?.plan_expires_at ?? null)
					setStage('paid')
					void refreshSessionUser()
				} else if (r.order.status === 'closed' || r.order.status === 'refunded') {
					stopPoll()
					setError('这笔订单已关闭，请重新下单')
					setStage('idle')
				}
			} catch {
				// 偶发失败（网络抖动、后端重启）不打断轮询，下一轮自然补上
			}
		}, POLL_MS)
		return stopPoll
	}, [stage, order, stopPoll])

	if (!open) return null
	const featureName = PLUS_FEATURE_NAMES[props.feature ?? 'generic']
	const picked = prices.find(p => p.months === months) ?? prices[0]
	const plus = isPlusUser(user)
	// 无期限 Plus（管理员手工授予）：plan_expires_at 为 null 且已是 plus。
	// 这种账号买不成也续不了 —— 核销后反而会把"永久"压成一个月。
	const perpetual = plus && !user?.plan_expires_at
	const expiresNow = fmtExpiry(user?.plan_expires_at ?? null)
	const wechat = isWechatBrowser()

	return (
		<div className={classes.backdrop} onClick={onClose}>
			<div className={classes.box} role="dialog" aria-modal="true" aria-label="升级 Plus 会员"
				onClick={e => e.stopPropagation()}>
				{/* 已开通态不显示副标题与有效期：正文里已经说了，再写一遍就成了两句一样的话 */}
				<div className={classes.head}>
					<h3 className={classes.title}>
						{stage === 'paid' ? '升级成功' : plus ? 'Plus 会员' : '升级 Plus 会员'}
					</h3>
					{stage !== 'paid' && (
						<div className={classes.sub}>
							{perpetual
								? <>这个账号是无限期 Plus —— 没有到期时间，不需要续费。</>
								: plus
									? <>再买一份是「顺延」—— 到期时间从现在的到期日往后加，不会浪费剩余天数。</>
									: <>「<span className={classes.feature}>{featureName}</span>」是 Plus 会员功能，开通后立刻可用。</>}
						</div>
					)}
					{stage !== 'paid' && plus && expiresNow && (
						<div className={classes.plusState}>有效期至 {expiresNow}</div>
					)}
				</div>

				{stage === 'waiting' && order ? (
					<div className={classes.body}>
						<div className={classes.payPanel}>
							<div className={classes.payAmount}>
								<span className={classes.payAmountUnit}>¥</span>{order.amount.toFixed(2)}
							</div>
							<div className={classes.payDesc}>
								{order.months} 个月 Plus 会员 · 用微信扫下面的码
							</div>
							<div className={classes.qrBox}>
								{order.qr
									? <img className={classes.qrImg} src={order.qr} alt="微信支付二维码" />
									: <div className={classes.qrFallback}>{order.code_url || '二维码生成失败'}</div>}
							</div>
							<div className={classes.payTips}>
								{wechat
									? '在微信里扫不了自己这块屏：长按二维码保存，再用「扫一扫 → 相册」打开'
									: '打开微信「扫一扫」，对着屏幕扫；付完这一页会自动变成「已开通」'}
								<br />付完约 1 分钟内自动开通，不用守着这一页
							</div>
						</div>
					</div>
				) : stage === 'paid' ? (
					<div className={classes.body}>
						<div className={classes.payPanel}>
							<div className={classes.payOkMark}>✓</div>
							<div className={classes.payAmount}>已开通</div>
							<div className={classes.payDesc}>
								{expires ? <>Plus 会员有效期至 {fmtExpiry(expires)}</> : 'Plus 会员已生效'}
								<br />曲谱上限 200 首、跟弹与自动左手伴奏、完整练习面板都开了
							</div>
						</div>
					</div>
				) : (
					perpetual ? (
						<div className={classes.body}>
							<div className={classes.payPanel}>
								<div className={classes.payOkMark}>✓</div>
								<div className={classes.payAmount}>长期有效</div>
								<div className={classes.payDesc}>
									曲谱上限 200 首、跟弹与自动左手伴奏、完整练习面板都已开放。
								</div>
							</div>
						</div>
					) : (
					<div className={classes.body}>
						<div className={classes.buyBlock}>
							<div className={classes.buyHd}>
								<span className={classes.buyTitle}>{plus ? '续费' : '开通'}</span>
								<span className={classes.buySub}>
									一次买断这一段时长，到期不自动扣款
								</span>
							</div>
							<div className={classes.plans}>
								{prices.map(p => (
									<div key={p.months} role="radio" aria-checked={p.months === months}
										tabIndex={0}
										className={`${classes.planCard} ${p.months === months ? classes.planCardOn : ''}`}
										onClick={() => setMonths(p.months)}
										onKeyDown={e => {
											if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setMonths(p.months) }
										}}>
										{p.badge && <span className={classes.planBadge}>{p.badge}</span>}
										<span className={classes.planName}>{p.label}</span>
										<span className={classes.planPrice}>
											<span className={classes.planUnit}>¥</span>{p.price}
										</span>
										<span className={classes.planNote}>
											{splitNote(p.note).map((part, i) => (
												<span key={i} className={i > 0 ? classes.noteTail : undefined}>{part}</span>
											))}
										</span>
									</div>
								))}
							</div>
							{error && <p className={classes.payErr}>{error}</p>}
							{needLogin && (
								<p className={classes.payErr}>
									购买需要先登录。
									<button className={classes.tb} style={{padding: '2px 6px', marginLeft: 4}}
										onClick={() => { onClose(); navigateHash('#login') }}>去登录</button>
								</p>
							)}
							{!payEnabled && (
								<p className={classes.payErr}>微信支付暂未开通，请联系站长开通。</p>
							)}
						</div>
						<button className={classes.tableToggle} aria-expanded={showTable}
							onClick={() => setShowTable(v => !v)}>
							<span>{showTable ? '收起对比表' : '免费会员与 Plus 会员的全部差别'}</span>
							<span className={classes.tableCaret} aria-hidden="true">{showTable ? '▲' : '▼'}</span>
						</button>
						{showTable && <>
						<div className={classes.table}>
							<div className={`${classes.line} ${classes.headRow}`}>
								<div className={`${classes.th} ${classes.thName}`}>功能</div>
								<div className={classes.th}>免费会员</div>
								<div className={`${classes.th} ${classes.thPlus}`}>Plus 会员</div>
							</div>
							{PLAN_FEATURES.map((row: PlanFeatureRow) => (
								<div className={classes.row} key={row.name}>
									<div className={classes.line}>
										<div className={`${classes.cell} ${classes.cellName}`}>{row.name}</div>
										<div className={classes.cell}>{cellContent(row.free, classes)}</div>
										<div className={`${classes.cell} ${classes.cellPlus}`}>{cellContent(row.plus, classes)}</div>
									</div>
									{row.details?.map(d => (
										<div className={classes.detail} key={d.label}>
											<span className={`${classes.tag} ${d.who === 'both' ? classes.tagBoth : classes.tagPlus}`}>
												{d.who === 'both' ? '两档' : 'Plus'}
											</span>
											<span className={classes.detailText}>
												<b className={classes.detailLabel}>{d.label}</b>{d.text}
											</span>
										</div>
									))}
								</div>
							))}
						</div>
						<p className={classes.hint}>
							曲谱数量按「已存 / 上限」计算，删掉不需要的曲谱会立刻释放名额。
						</p>

						</>}
					</div>
					)
				)}

				<div className={classes.acts}>
					{stage === 'waiting' && <span className={classes.waiting}>等待微信支付…（付完自动开通）</span>}
					{stage === 'paid'
						? <button className={`${classes.tb} ${classes.tbPrimary}`} onClick={onClose}>开始使用</button>
						: stage === 'waiting'
							? <button className={`${classes.tb} ${classes.tbOutline}`} onClick={onClose}>关闭</button>
							: perpetual
								? <button className={`${classes.tb} ${classes.tbPrimary}`} onClick={onClose}>知道了</button>
								: <>
									<button className={`${classes.tb} ${classes.tbOutline}`} onClick={onClose}>以后再说</button>
									<button className={`${classes.tb} ${classes.tbPrimary}`}
										disabled={stage === 'creating' || !payEnabled}
										onClick={() => void startPay()}>
										{stage === 'creating'
											? '正在下单…'
											: picked ? `立即支付 ¥${picked.price}` : '立即支付'}
									</button>
								</>}
				</div>
			</div>
		</div>
	)
}
